import React, { useEffect, useState } from "react";
import { UPDATE_USER_INFO } from "../../../../utils/GraphQL/mutations";
import "./userInfoModal.css";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import phoneNumberFormat from "../../../../utils/formatters/phoneNumberFormat";
import UpdateUserInfoTest from "../../../../utils/formValidators/userUpdateInfoCheck";
import { useMutation } from "@apollo/client";
import Auth from "../../../../utils/auth";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";

const UserInfoModal = ({ user, updateModalHandler }) => {
  const [numbers, setNumbers] = useState();
  const [saving, setSaving] = useState(false);
  const [updatedInformation, setUpdatedInformation] = useState({});
  const [invalidErrors, setInvalidErrors] = useState();
  const [updateInfoMutation] = useMutation(UPDATE_USER_INFO);
  const [initLoginObj, setInitLoginObj] = useState({
    id: "",
    city: "",
    email: "",
    firstName: "",
    lastName: "",
    initLogin: true,
    phoneNumberIds: [],
    state: "",
    streetAddress: "",
    zipCode: "",
    prevPass: "",
    newPass: "",
  });

  // form validation class
  const updateTest = new UpdateUserInfoTest(updatedInformation);

  useEffect(() => {
    if (user !== undefined) {
      console.log(user);
      const formattedNumbers =
        user?.phoneNumberIds.length > 0
          ? user?.phoneNumberIds.map((num, index) => {
              return {
                ...num,
                phoneNumber: phoneNumberFormat(num.phoneNumber),
              };
            })
          : [
              {
                _id: "newNum",
                phoneNumber: phoneNumberFormat("0000000000"),
                numberType: "Cell",
              },
            ];

      setNumbers(formattedNumbers);

      setInitLoginObj({
        id: user?._id,
        city: user?.city || "",
        email: user?.email || "",
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        initLogin: user?.initLogin,
        phoneNumberIds: formattedNumbers || [],
        state: user?.state || "",
        streetAddress: user?.streetAddress || "",
        zipCode: user?.zipCode || "",
        prevPass: "",
        newPass: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    setUpdatedInformation({ ...initLoginObj, phoneNumberIds: numbers });
  }, [initLoginObj, numbers]);

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (
      name === "firstName" ||
      name === "city" ||
      name === "state" ||
      name === "streetAddress" ||
      name === "lastName"
    ) {
      value = capitalizeFirstLetters(value);

      setInitLoginObj((prev) => {
        return { ...prev, [name]: value };
      });

      return;
    }

    setInitLoginObj((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const handlePhoneNumber = (event, index) => {
    const id = event.target.name;
    const filteredNumber = event.target.value.replace(/[^0-9]/gi, "");

    const filterUpdate = updatedInformation.phoneNumberIds.map((number) => {
      if (id === number._id) {
        return { ...number, phoneNumber: phoneNumberFormat(filteredNumber) };
      }
      return number;
    });

    setNumbers(filterUpdate);
  };

  // Takes the domain directly rather than reading it back out of the button's
  // innerText — the text is the data, and a styled button can wrap its label
  // in a child element, which would make event.target the wrong node.
  const autoEmailAdd = (atDomain) => {
    const email = initLoginObj?.email.split("@")[0];

    setInitLoginObj((prev) => {
      return { ...prev, email: `${email}${atDomain}` };
    });
  };

  const submitUpdateInfo = async () => {
    const test = await updateTest.updateValid();
    if (test === true) {
      setInvalidErrors();
      const stripPhoneNumArr = updatedInformation.phoneNumberIds.map((num) => {
        return {
          _id: num._id,
          phoneNumber: num.phoneNumber,
          numberType: "Cell",
          title: num.title,
        };
      });

      const { data: updatedUser } = await updateInfoMutation({
        variables: {
          updateUserId: updatedInformation.id,
          firstName: updatedInformation.firstName,
          lastName: updatedInformation.lastName,
          email: updatedInformation.email,
          phoneNumberIds: stripPhoneNumArr,
          streetAddress: updatedInformation.streetAddress,
          city: updatedInformation.city,
          state: updatedInformation.state,
          zipCode: updatedInformation.zipCode,
          newPass: updatedInformation.newPass,
          prevPass: updatedInformation.prevPass,
          initLogin: updatedInformation.initLogin,
        },
      });

      setSaving(false);
      Auth.resetToken(updatedUser?.updateUser.token);
      return;
    }

    setInvalidErrors(test);
    setSaving(false);
    return;
  };

  // A first-login user has to complete this form before reaching the app, so
  // there is no way out of it: no ×, no Esc, no backdrop click. All three have
  // to agree — hiding only the × would make it LOOK mandatory while two other
  // exits still work.
  const canDismiss = initLoginObj.initLogin === false;

  return (
    <BdrModal
      open
      onClose={() => updateModalHandler(false)}
      title={canDismiss ? "Update Information" : "Please Enter Your Information"}
      variant="panel"
      size="lg"
      showClose={canDismiss}
      closeOnBackdrop={canDismiss}
      closeOnEsc={canDismiss}
      actions={
        <BdrButton
          /* solid, not subdued: this modal is variant="panel", i.e. the
             saturated --bdr-grad-panel, where a tinted fill muddies into the
             background. Style guide §6b — the surface picks the weight. */
          variant="confirm-solid"
          disabled={saving}
          onClick={() => {
            setSaving(true);
            submitUpdateInfo();
          }}
        >
          {saving ? "Saving..." : "Update"}
        </BdrButton>
      }
    >
      {saving && <AdminBDRSpinner message={"Saving..."} />}

      {invalidErrors?.length > 0 && (
        <div className="bdr-alert bdr-alert--danger bdr-alert--start uim-errors">
          <ul>
            {invalidErrors.map((error, index) => (
              <li key={error + index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="bdr-modal-section">
        <h4 className="bdr-modal-section-label">Name</h4>
        <div className="user-info-modal-sec">
            <BdrInput
              className="uim-f-half"
              type="text"
              label="First Name"
              name="firstName"
              inputMode="text"
              value={initLoginObj.firstName}
              onChange={handleChange}
            />
            <BdrInput
              className="uim-f-half"
              type="text"
              label="Last Name"
              name="lastName"
              inputMode="text"
              value={initLoginObj.lastName}
              onChange={handleChange}
            />
        </div>
      </div>

      <div className="bdr-modal-section">
        <h4 className="bdr-modal-section-label">Contact</h4>
        <div className="user-info-modal-sec">
          <BdrInput
            className="uim-f-full"
            type="text"
            label="Email"
            inputMode="text"
            name="email"
            value={initLoginObj.email}
            onChange={handleChange}
          />
        </div>
        <div className="user-info-modal-sec emails">
          {["@gmail.com", "@yahoo.com", "@icloud.com"].map((domain) => (
            <BdrButton
              key={domain}
              size="sm"
              onClick={() => autoEmailAdd(domain)}
            >
              {domain}
            </BdrButton>
          ))}
        </div>
        <div className="user-info-modal-sec">
            {initLoginObj?.phoneNumberIds?.map((phoneNum, index) => {
              return (
                <BdrInput
                  key={phoneNum?.phoneNumber + index}
                  className="uim-f-half"
                  type="tel"
                  label="Phone Number"
                  maxLength={12}
                  inputMode="numeric"
                  name={phoneNum?._id}
                  value={numbers[index].phoneNumber}
                  onChange={(event) => {
                    handlePhoneNumber(event, index);
                  }}
                />
              );
            })}
        </div>
      </div>

      <div className="bdr-modal-section">
        <h4 className="bdr-modal-section-label">Address</h4>
        <div className="user-info-modal-sec">
          <BdrInput
            className="uim-f-full"
            type="text"
            label="Street Address"
              inputMode="text"
              name="streetAddress"
              value={initLoginObj.streetAddress}
              onChange={handleChange}
            />
          </div>
          <div className="user-info-modal-sec">
            <BdrInput
              className="uim-f-grow"
              type="text"
              label="City"
              inputMode="text"
              name="city"
              value={initLoginObj.city}
              onChange={handleChange}
            />
            <BdrInput
              className="uim-f-tiny"
              type="text"
              label="State"
              inputMode="text"
              name="state"
              value={initLoginObj.state}
              onChange={handleChange}
            />
            <BdrInput
              className="uim-f-small"
              type="text"
              label="Zip Code"
              inputMode="text"
              name="zipCode"
              value={initLoginObj.zipCode}
              onChange={handleChange}
            />
          </div>
      </div>

      <div className="bdr-modal-section">
        <h4 className="bdr-modal-section-label">Password</h4>
        <p className="bdr-field-helper uim-rules">
          Must be longer than 8 characters, and include at least one uppercase,
          one lowercase and one number.
          {canDismiss &&
            " To change it, enter both your previous and your new password."}
        </p>
        <div className="user-info-modal-sec">
            {initLoginObj.initLogin === false && (
              <BdrInput
                className="uim-f-half"
                type="password"
                label="Previous Password"
                inputMode="text"
                name="prevPass"
                value={initLoginObj?.prevPass}
                onChange={handleChange}
              />
            )}
            <BdrInput
              className="uim-f-half"
              type="password"
              label="Enter New Password"
              inputMode="text"
              name="newPass"
              value={initLoginObj?.newPass}
              onChange={handleChange}
            />
        </div>
      </div>
    </BdrModal>
  );
};

export default UserInfoModal;
